import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  ElementRef,
  input,
  OnChanges,
  OnInit,
  Optional,
  Renderer2,
  SimpleChanges
} from '@angular/core';
import { takeUntil } from 'rxjs/operators';
import { FireflyModalService } from '../../../modal';
import { Breakpoint, NestedValuePipe } from '../../../utils';
import { FireflyLocalizationService } from '../../../utils/localization/firefly-localization.service';
import { GroupedMultipleItem } from '../../models/grouped-item.model';
import { NestedListData } from '../../models/nested-list-data.model';
import { NestedSuggesterParent } from '../../models/nested-suggester-parent.interface';
import { FireflyMultipleSuggesterComponent } from '../multiple-suggester/multiple-suggester.component';

@Component({
  standalone: false,
  selector: 'f-multiple-suggester-with-nested-items',
  templateUrl: './multiple-suggester-with-nested-items.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FireflyMultipleSuggesterWithNestedItemsComponent
  extends FireflyMultipleSuggesterComponent
  implements OnInit, OnChanges, NestedSuggesterParent
{
  nestedLevels = input<number>(4);
  childrenPath = input<string>('children');
  subCategoryPath = input<string>('sector');
  levelIndentation = input<number>(20);
  expandedByDefault = input<boolean>(false);
  maxLastLevelItemsCount = input<number | undefined>(undefined);
  maxLastLevelItemsMessage = input<string | undefined>(undefined);
  dropdownMaxCustomHeight?: number;
  private readonly dropdownHeightForNestedItems = 290;
  expandedItems: Set<string> = new Set();
  private matchedItemIds: Set<string> = new Set();
  private filteredChildrenMap: Map<string, NestedListData[]> = new Map();
  private nodeMap: Map<string, NestedListData> = new Map();
  private allSelectedCache: Map<string, boolean> = new Map();
  private someSelectedCache: Map<string, boolean> = new Map();
  private selectedLeafCountCache: number | null = null;

  private _childrenPathInitialized = false;

  constructor(
    public override host: ElementRef,
    renderer: Renderer2,
    nestedValuePipe: NestedValuePipe,
    changeDetectorRef: ChangeDetectorRef,
    modalService: FireflyModalService,
    @Optional() localizationService: FireflyLocalizationService
  ) {
    super(host, renderer, nestedValuePipe, changeDetectorRef, modalService, localizationService);
    effect(() => {
      this.childrenPath(); // track signal
      if (!this._childrenPathInitialized) {
        this._childrenPathInitialized = true;
        return; // ngOnChanges handles the initial build
      }
      this.buildNodeMap();
      this.clearSelectionCache();
    });
  }

  override ngOnInit() {
    super.ngOnInit();
    this.dropdownMaxCustomHeight = window.innerWidth > Breakpoint.Sm ? this.dropdownHeightForNestedItems : undefined;
    const manager = this.suggesterManager as unknown as { getFilterOptions: () => void };
    const originalGetFilterOptions = manager.getFilterOptions;
    manager.getFilterOptions = () => {
      if (!this.customFilterMode) {
        this.filterNestedItems();
      } else {
        originalGetFilterOptions.call(this.suggesterManager);
      }
    };
    this.inputValue.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(val => {
      const trimmed = ((val as string) || '').trim();
      if (trimmed.length < 3 && !this.customFilterMode) {
        this.resetSearchState();
      }
    });
  }

  private buildNodeMap(): void {
    this.nodeMap.clear();
    const traverse = (items: NestedListData[]) => {
      for (const item of items) {
        this.nodeMap.set(item[this.idField] as string, item);
        const children = this.nestedValuePipe.transform(item, this.childrenPath()) as NestedListData[];
        if (children?.length) traverse(children);
      }
    };
    traverse(this.items as NestedListData[]);
  }

  private clearSelectionCache(): void {
    this.allSelectedCache.clear();
    this.someSelectedCache.clear();
    this.selectedLeafCountCache = null;
  }

  // "Last level" means leaf items (no children), regardless of how deep the tree is.
  private getSelectedLeafCount(): number {
    if (this.selectedLeafCountCache !== null) {
      return this.selectedLeafCountCache;
    }
    let count = 0;
    const traverse = (items: NestedListData[]) => {
      for (const item of items) {
        const children = this.nestedValuePipe.transform(item, this.childrenPath()) as NestedListData[];
        if (children?.length) {
          traverse(children);
        } else if (item.isSelected) {
          count++;
        }
      }
    };
    traverse(this.items as NestedListData[]);
    this.selectedLeafCountCache = count;
    return count;
  }

  isDisabledByLevelLimit(item: NestedListData): boolean {
    const limit = this.maxLastLevelItemsCount();
    if (!limit || limit <= 0) return false;

    // A fully selected leaf or section must stay enabled so it can still be unselected.
    const isFullySelected = this.hasChildren(item) ? this.areAllChildrenSelected(item) : item.isSelected;
    if (isFullySelected) return false;

    return this.getSelectedLeafCount() >= limit;
  }

  getLevelLimitMessage(): string {
    const limit = this.maxLastLevelItemsCount();
    return this.maxLastLevelItemsMessage() || `Maximum ${limit} items reached`;
  }

  // When selecting a parent node whose leaf descendants exceed the remaining capacity,
  // only select as many as fit (in tree order) and leave the rest unselected/blocked.
  private selectDescendantsUpToLimit(items: NestedListData[], parentSector?: string): void {
    let remaining = Math.max((this.maxLastLevelItemsCount() ?? 0) - this.getSelectedLeafCount(), 0);

    const traverse = (nodes: NestedListData[], sector?: string) => {
      for (const node of nodes) {
        const sectorValue = this.ensureItemSector(node, sector);
        const children = this.isFilteringActive() ? this.getOriginalChildren(node) : this.getChildren(node);

        if (children.length > 0) {
          traverse(children, sectorValue);
        } else if (!node.isSelected && remaining > 0) {
          node.isSelected = true;
          remaining--;
        }
      }
    };

    traverse(items, parentSector);
  }

  override ngOnChanges(changes: SimpleChanges) {
    super.ngOnChanges(changes);

    if (changes['idField'] && !changes['items']) {
      // idField key used to build nodeMap changed without a simultaneous items change
      this.buildNodeMap();
      this.clearSelectionCache();
    }

    if (changes['items']) {
      this.buildNodeMap();
      this.clearSelectionCache();
      if (this.expandedByDefault()) {
        this.expandAllItems();
      }
      // Re-apply search filter when items change
      if (this.suggesterManager.inputControlValue) {
        this.filterNestedItems();
      }

      if (changes['items'].firstChange) {
        if (this.selectedChips.length > 0) {
          this.syncSelectionToTree();
        } else {
          this.updateParentStates();
          this.rebuildChips();
        }
        this.changeDetectorRef.markForCheck();
      }
    }
  }

  private filterNestedItems(): void {
    const searchString = this.suggesterManager.inputControlValue.toLowerCase();

    if (searchString.length < 3) {
      this.resetSearchState();
      return;
    }

    this.matchedItemIds.clear();
    this.filteredChildrenMap.clear();
    this.clearSelectionCache();

    const filteredGroups = this.suggesterManager.initialGroupedData.groupedItems
      .map(group => {
        const filteredItems = this.filterTreeRecursively(group.value as NestedListData[], searchString);
        return {
          ...group,
          value: filteredItems
        };
      })
      .filter(group => group.value.length > 0);

    const totalLength = filteredGroups.reduce((sum, group) => sum + this.countAllItems(group.value), 0);

    this.suggesterManager.listData = {
      groupedItems: filteredGroups,
      totalLength
    };

    this.autoExpandMatchedItems();
  }

  private filterTreeRecursively(items: NestedListData[], searchString: string): NestedListData[] {
    const filtered: NestedListData[] = [];

    for (const item of items) {
      // Only search by 'name' field
      const itemName = ((item.name as string) || '').toLowerCase();
      const children = this.getChildren(item);

      // Recursively filter children first
      const filteredChildren = children.length > 0 ? this.filterTreeRecursively(children, searchString) : [];

      const itemMatches = itemName.includes(searchString);
      const hasMatchingChildren = filteredChildren.length > 0;

      // Include item if it matches or has matching descendants
      if (itemMatches || hasMatchingChildren) {
        if (itemMatches) {
          this.matchedItemIds.add(item[this.idField] as string);
        }

        // Always store filtered children to hide non-matching descendants during search
        if (children.length > 0) {
          this.filteredChildrenMap.set(item[this.idField] as string, filteredChildren);
        }

        // IMPORTANT: Use the original item reference to maintain checkbox state
        filtered.push(item);
      }
    }

    return filtered;
  }

  private countAllItems(items: NestedListData[]): number {
    let count = 0;
    for (const item of items) {
      count++;
      const children = this.nestedValuePipe.transform(item, this.childrenPath()) as NestedListData[];
      if (children && children.length > 0) {
        count += this.countAllItems(children);
      }
    }
    return count;
  }

  private autoExpandMatchedItems(): void {
    // Expand all items that contain matches in their subtree
    const expandItemsWithMatches = (items: NestedListData[]) => {
      for (const item of items) {
        const children = this.getChildren(item);
        if (children.length > 0) {
          const hasMatchInSubtree = this.hasMatchInSubtree(item);
          if (hasMatchInSubtree) {
            this.expandedItems.add(item[this.idField] as string);
            expandItemsWithMatches(children);
          }
        }
      }
    };

    const groups = this.suggesterManager.listData.groupedItems || [];
    for (const group of groups) {
      expandItemsWithMatches(group.value as NestedListData[]);
    }
  }

  private hasMatchInSubtree(item: NestedListData): boolean {
    const itemId = item[this.idField] as string;
    if (this.matchedItemIds.has(itemId)) {
      return true;
    }

    const children = this.getChildren(item);
    return children.some(child => this.hasMatchInSubtree(child));
  }

  override onFocus(): void {
    super.onFocus();
    if (this.suggesterManager.inputControlValue && !this.customFilterMode) {
      this.filterNestedItems();
    }
  }

  private resetSearchState(): void {
    // Reset to show all items when clearing search
    this.matchedItemIds.clear();
    this.filteredChildrenMap.clear();
    this.clearSelectionCache();
    this.suggesterManager.listData = {
      groupedItems: this.suggesterManager.initialGroupedData.groupedItems.slice(),
      totalLength: this.suggesterManager.initialGroupedData.totalLength
    };
    this.updateParentStates();
    this.changeDetectorRef.markForCheck();
  }

  toggleExpand(item: NestedListData, event?: Event): void {
    event?.stopPropagation();
    const itemId = item[this.idField] as string;

    if (this.expandedItems.has(itemId)) {
      this.expandedItems.delete(itemId);
    } else {
      this.expandedItems.add(itemId);
    }

    this.changeDetectorRef.markForCheck();
  }

  isExpanded(item: NestedListData): boolean {
    return this.expandedItems.has(item[this.idField] as string);
  }

  hasChildren(item: NestedListData): boolean {
    const children = this.nestedValuePipe.transform(item, this.childrenPath());
    return Array.isArray(children) && children.length > 0;
  }

  getChildren(item: NestedListData): NestedListData[] {
    // Check if we have filtered children for this item (during search)
    const itemId = item[this.idField] as string;
    if (this.filteredChildrenMap.has(itemId)) {
      return this.filteredChildrenMap.get(itemId)!;
    }
    // Otherwise return the actual children
    return this.nestedValuePipe.transform(item, this.childrenPath()) || [];
  }

  private traverseTree(
    items: NestedListData[],
    callback: (item: NestedListData, parentSector?: string) => void,
    parentSector?: string
  ): void {
    items.forEach(item => {
      const currentSector = (item[this.subCategoryPath()] as string) || parentSector;
      callback(item, currentSector);

      if (this.hasChildren(item)) {
        this.traverseTree(this.getChildren(item), callback, currentSector);
      }
    });
  }

  private expandAllItems(): void {
    this.traverseTree(this.items as NestedListData[], item => {
      if (this.hasChildren(item)) {
        this.expandedItems.add(item[this.idField] as string);
      }
    });
  }

  private setSelectionRecursively(item: NestedListData, isSelected: boolean, parentSector?: string): void {
    const sectorValue = this.ensureItemSector(item, parentSector);
    item.isSelected = isSelected;
    const childrenForSelection = this.isFilteringActive() ? this.getOriginalChildren(item) : this.getChildren(item);
    childrenForSelection.forEach(child => this.setSelectionRecursively(child, isSelected, sectorValue));
  }

  private ensureItemSector(item: NestedListData, parentSector?: string): string | undefined {
    const sectorValue = (item[this.subCategoryPath()] as string) || parentSector;
    if (sectorValue && !item[this.subCategoryPath()]) {
      item[this.subCategoryPath()] = sectorValue;
    }
    return sectorValue;
  }

  areAllChildrenSelected(item: NestedListData): boolean {
    const id = item[this.idField] as string;
    if (this.allSelectedCache.has(id)) {
      return this.allSelectedCache.get(id)!;
    }
    const children = this.isFilteringActive() ? this.getOriginalChildren(item) : this.getChildren(item);
    const result =
      children.length > 0 &&
      children.every(child => child.isSelected && (!this.hasChildren(child) || this.areAllChildrenSelected(child)));
    this.allSelectedCache.set(id, result);
    return result;
  }

  private areSomeChildrenSelected(item: NestedListData): boolean {
    const id = item[this.idField] as string;
    if (this.someSelectedCache.has(id)) {
      return this.someSelectedCache.get(id)!;
    }
    const children = this.isFilteringActive() ? this.getOriginalChildren(item) : this.getChildren(item);
    const result =
      children.length > 0 && children.some(child => child.isSelected || this.areSomeChildrenSelected(child));
    this.someSelectedCache.set(id, result);
    return result;
  }

  isIndeterminate(item: NestedListData): boolean {
    return this.hasChildren(item) && this.areSomeChildrenSelected(item) && !this.areAllChildrenSelected(item);
  }

  private findItemById(_items: NestedListData[], id: string): NestedListData | null {
    return this.nodeMap.get(id) ?? null;
  }

  private refreshListData(): void {
    this.suggesterManager.groupData(this.listOfCheckedItems, this.categoryPath);
    this.changeDetectorRef.markForCheck();
  }

  override onRemoveFromChips($event: unknown): void {
    const removedItem = $event as NestedListData;
    const itemInTree = this.findItemById(this.items as NestedListData[], removedItem[this.idField] as string);

    if (itemInTree) {
      this.setSelectionRecursively(itemInTree, false);
      this.updateParentStates();
    } else {
      const original = this.findOriginalNodeById(removedItem[this.idField] as string);
      if (original) {
        this.setSelectionRecursively(original, false);
        this.updateParentStates();
      }
    }

    this.rebuildChips();

    if (this.selectedChips.length < 1) {
      this.expandedItems.clear();
      this.changeDetectorRef.markForCheck();
    }

    this.onRemove(removedItem);
    this.refreshListData();
    this.emitSelectedChipsClick();
  }

  override onCheckboxChange(item: NestedListData, _category?: unknown): void {
    if (this.isDisabledByLevelLimit(item)) return;

    const children = this.isFilteringActive() ? this.getOriginalChildren(item) : this.getChildren(item);

    if (!children || children.length === 0) {
      item.isSelected = !item.isSelected;
      this.ensureSectorInherited(item);
      // Immediately repaint the checkbox, then do the full-tree work in the next macrotask.
      // updateParentStates + rebuildChips walk the entire tree (O(n)) and would otherwise
      // block the frame, causing a visible delay before the checkbox state is rendered.
      this.changeDetectorRef.markForCheck();
      setTimeout(() => {
        this.updateParentStates();
        this.rebuildChips();
        this.refreshListData();
      });
      return;
    }

    const allChildrenSelected = this.areAllChildrenSelected(item);
    const newState = !allChildrenSelected;

    item.isSelected = newState;

    // Immediately repaint the clicked parent checkbox, then propagate to descendants and
    // update derived state in the next macrotask. setSelectionRecursively can visit
    // thousands of nodes (e.g. a country with all its states/districts/cities), so keeping
    // it synchronous would block the frame just like the leaf-node case above.
    this.changeDetectorRef.markForCheck();
    setTimeout(() => {
      const sectorValue = item[this.subCategoryPath()] as string;
      const limit = this.maxLastLevelItemsCount();
      if (newState && limit && limit > 0) {
        this.selectDescendantsUpToLimit(children, sectorValue);
      } else {
        children.forEach(child => this.setSelectionRecursively(child, newState, sectorValue));
      }
      this.updateParentStates();
      this.rebuildChips();
      this.refreshListData();
    });
  }

  override onItemClick(_item: unknown): void {
    // Selection updates are queued in onCheckboxChange with setTimeout.
    // Emit on the next macrotask so itemClick carries the latest selectedChips.
    setTimeout(() => {
      this.emitSelectedChipsClick();
    });
  }

  private updateParentStates(): void {
    this.clearSelectionCache();
    const updateRecursively = (items: NestedListData[]) => {
      items.forEach(item => {
        const children = this.isFilteringActive() ? this.getOriginalChildren(item) : this.getChildren(item);
        if (children && children.length > 0) {
          updateRecursively(children);
          item.isSelected = this.areAllChildrenSelected(item);
        }
      });
    };

    updateRecursively(this.items as NestedListData[]);
  }

  private syncSelectionToTree(): void {
    this.clearSelectionCache();
    const clearAll = (items: NestedListData[]) => {
      items.forEach(item => {
        item.isSelected = false;
        const children = this.nestedValuePipe.transform(item, this.childrenPath()) as NestedListData[];
        if (children?.length) clearAll(children);
      });
    };
    clearAll(this.items as NestedListData[]);

    this.selectedChips.forEach(chip => {
      const itemInTree = this.findItemById(this.items as NestedListData[], chip[this.idField] as string);
      if (itemInTree) {
        this.setSelectionRecursively(itemInTree, true);
      }
    });

    this.updateParentStates();
  }

  // Minimal helper to detect active search filter
  private isFilteringActive(): boolean {
    const val = this.suggesterManager.inputControlValue;
    return !!(val && (val as string).trim().length > 0 && !this.customFilterMode);
  }

  // Find original node in initial grouped data by id (O(1) via nodeMap)
  private findOriginalNodeById(idToFind: string): NestedListData | null {
    return this.nodeMap.get(idToFind) ?? null;
  }

  private getOriginalChildren(item: NestedListData): NestedListData[] {
    // Read children directly from the item rather than via getChildren(), which may return
    // a filtered subset from filteredChildrenMap. Child array references are preserved through
    // filterTreeRecursively, so this always reaches the full, unfiltered descendants regardless
    // of whether the item originated from this.items or initialGroupedData.
    return (this.nestedValuePipe.transform(item, this.childrenPath()) as NestedListData[]) || [];
  }

  private rebuildChips(): void {
    const newSelectedChips: NestedListData[] = [];
    const addedToChips = new Set<string>();
    for (const chip of this.selectedChips) {
      const id = chip[this.idField] as string;
      const treeItem = this.findItemById(this.items as NestedListData[], id);
      if (treeItem && (treeItem.isSelected || this.areAllChildrenSelected(treeItem))) {
        const parentSector = this.findParentSector(this.items as NestedListData[], id);
        this.ensureItemSector(treeItem, parentSector ?? undefined);
        newSelectedChips.push(treeItem);
        addedToChips.add(id);
      }
    }

    const collectChips = (items: NestedListData[], parentSector?: string) => {
      for (const item of items) {
        const itemId = item[this.idField] as string;
        if (addedToChips.has(itemId)) continue;

        const sectorValue = this.ensureItemSector(item, parentSector);

        const children = this.isFilteringActive() ? this.getOriginalChildren(item) : this.getChildren(item);

        if (children && children.length > 0) {
          if (this.areAllChildrenSelected(item)) {
            // Remove any descendant chips that were preserved from the previous state
            // (e.g. a single child chip that now gets consolidated into the parent chip)
            const descendantIds = new Set<string>();
            this.markDescendantsAsAdded(item, descendantIds);
            for (let i = newSelectedChips.length - 1; i >= 0; i--) {
              const chipId = newSelectedChips[i][this.idField] as string;
              if (descendantIds.has(chipId)) {
                newSelectedChips.splice(i, 1);
                addedToChips.delete(chipId);
              }
            }
            newSelectedChips.push(item);
            this.markDescendantsAsAdded(item, addedToChips);
          } else {
            collectChips(children, sectorValue);
          }
        } else if (item.isSelected) {
          newSelectedChips.push(item);
          addedToChips.add(itemId);
        }
      }
    };

    // When filtering is active, iterate over the original grouped items so non-matching
    // descendants (that may be hidden in the filtered view) are considered for chips.
    if (this.isFilteringActive()) {
      const groups = this.suggesterManager.initialGroupedData.groupedItems as GroupedMultipleItem[];
      for (const group of groups) {
        collectChips(group.value as NestedListData[], undefined);
      }
    } else {
      collectChips(this.items as NestedListData[], undefined);
    }

    this.selectedChips = newSelectedChips;
  }

  private markDescendantsAsAdded(item: NestedListData, addedSet: Set<string>): void {
    addedSet.add(item[this.idField] as string);
    const children = this.isFilteringActive() ? this.getOriginalChildren(item) : this.getChildren(item);
    const addRecursive = (nodes: NestedListData[]) => {
      for (const n of nodes) {
        addedSet.add(n[this.idField] as string);
        const ch = this.isFilteringActive() ? this.getOriginalChildren(n) : this.getChildren(n);
        if (ch && ch.length) addRecursive(ch);
      }
    };
    if (children && children.length) addRecursive(children);
  }

  private ensureSectorInherited(item: NestedListData): void {
    if (item[this.subCategoryPath()]) return;

    const parentSector = this.findParentSector(this.items as NestedListData[], item[this.idField] as string);
    if (parentSector) {
      item[this.subCategoryPath()] = parentSector;
    }
  }

  private findParentSector(
    items: NestedListData[],
    targetId: string,
    parentSector?: string
  ): string | null | undefined {
    for (const item of items) {
      if (item[this.idField] === targetId) {
        return parentSector || null;
      }

      if (this.hasChildren(item)) {
        const currentSector = (item[this.subCategoryPath()] as string) || parentSector;
        const found = this.findParentSector(this.getChildren(item), targetId, currentSector);
        if (found !== undefined) return found;
      }
    }
    return undefined;
  }

  override onRemoveAll(): void {
    // Traverse the full original tree rather than the filtered view. When filtering is active,
    // getChildren() returns filtered children from filteredChildrenMap — matched leaf nodes have
    // an empty entry there, so traverseTree would stop short and never reach their descendants.
    const clearAll = (items: NestedListData[]) => {
      items.forEach(item => {
        item.isSelected = false;
        const children = this.nestedValuePipe.transform(item, this.childrenPath()) as NestedListData[];
        if (children?.length) clearAll(children);
      });
    };
    clearAll(this.items as NestedListData[]);
    this.clearSelectionCache();

    this.expandedItems.clear();

    super.onRemoveAll();
    this.refreshListData();
    this.changeDetectorRef.markForCheck();
  }

  override updateListOnActionButtonClick(event: boolean, _isForAllList?: boolean, _category?: unknown): void {
    if (!event) {
      this.onRemoveAll();
    } else {
      super.updateListOnActionButtonClick(event, _isForAllList ?? false, _category as GroupedMultipleItem);
      this.clearSelectionCache();
    }
  }

  override resetValue($event?: Event): void {
    this.resetSearchState();

    super.resetValue($event);
    this.refreshListData();
  }
}
